import type { TRecordType } from '@moneyforward/ap-frontend-components';
import type { ColumnType } from 'antd/es/table/interface';
import { isValidElement, type ReactNode } from 'react';

/**
 * Structural stand-in for rc-table's `RenderedCell`, which is not a direct
 * dependency here. Only `children` is ever read, so the full type is not needed.
 */
type RenderedCellLike = {
  children?: ReactNode;
  props?: object;
};

/**
 * Resolves antd's `dataIndex` (string | number | array) to a value.
 */
const getRecordValue = <RecordType extends TRecordType>(
  record: RecordType,
  dataIndex: ColumnType<RecordType>['dataIndex']
): unknown => {
  if (dataIndex === undefined || dataIndex === null) return undefined;
  const paths = Array.isArray(dataIndex) ? dataIndex : [dataIndex];
  return paths.reduce<unknown>((acc, path) => {
    if (acc === null || acc === undefined) return undefined;
    if (typeof acc !== 'object') return undefined;
    return Reflect.get(acc, path);
  }, record);
};

const isRenderedCell = (value: object): value is RenderedCellLike =>
  !isValidElement(value) &&
  !Array.isArray(value) &&
  ('children' in value || 'props' in value);

/**
 * Narrows a raw value from a column without `render` to something renderable.
 * Values that cannot be rendered (plain objects) become null — those columns
 * need an explicit `render`.
 */
const toReactNode = (value: unknown): ReactNode => {
  if (value === null || value === undefined) return null;
  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return value;
  }
  if (isValidElement(value)) return value;
  return null;
};

/**
 * Renders exactly what the real `<table>` layout has always shown: the
 * column's `render(value, record, index)` (or the raw value when there is
 * none). Deliberately does not know about `CardColumn`'s `cardRender` — that
 * is a card-layout-only concern (see `RecordCard`/`CardTable/util`).
 */
export const renderTableCell = <RecordType extends TRecordType>(
  column: ColumnType<RecordType>,
  record: RecordType,
  index: number
): ReactNode => {
  const value = getRecordValue(record, column.dataIndex);
  if (!column.render) return toReactNode(value);
  const rendered = column.render(value, record, index);
  // antd's colSpan/rowSpan control form — only `children` applies here.
  if (rendered !== null && typeof rendered === 'object') {
    if (isRenderedCell(rendered)) return rendered.children;
  }
  return rendered;
};
